/**
 * @fileoverview liquidefy-topology.ts — Liquidefy's real infra as
 *               the declarative desired-state value.
 * @module @liquidefy/network-deployment/topology
 * @license LQD Research LLC
 *
 * @author Scott Martin
 * @since 1.0.0 (n3)
 * @tier Infrastructure
 *
 * The single source of truth for what Liquidefy runs and how it is
 * deployed. Pure data — CONTROL-plane / browser-safe; no secrets,
 * no effecting. The §4 provider-selection is encoded here as the
 * bound set (the honest unbound connectors simply do not appear).
 * Targets form an acyclic DAG the substrate `wave` sequencer
 * orders. `satisfies` keeps it contract-true at compile time.
 */

import type { LiquidefyTopology } from "./topology-types.js";

export const LIQUIDEFY_TOPOLOGY = {
  environments: [
    { id: "prod", kind: "prod" },
    { id: "staging", kind: "staging" },
    { id: "ephemeral", kind: "ephemeral" },
  ],

  // The §4 LOCKED bound set, as data. Faces map to substrate
  // faces; only connectors Liquidefy actually runs appear.
  selection: {
    byFace: {
      platforms: ["fly", "cloudflare", "vercel"],
      routing: ["cloudflare"],
      tls: ["fly"],
      git: ["gitea"],
      registries: ["verdaccio", "gitea"],
      secrets: ["vault", "cloudflare", "fly"],
      database: ["supabase", "fly"],
      observability: ["cloudflare", "fly"],
      notifications: ["slack", "discord"],
      backup: ["fly"],
      ledger: ["composite"],
    },
  },

  // Deployables + the deploy-order DAG (dependsOn = "healthy
  // first"). DNS before TLS before cutover; the hub waits on the
  // git + registry it consumes.
  targets: [
    { id: "dns-cloudflare", face: "routing", connectorId: "cloudflare", dependsOn: [] },
    { id: "tls-fly", face: "tls", connectorId: "fly", dependsOn: ["dns-cloudflare"] },
    { id: "gitea-fly", face: "platforms", connectorId: "fly", dependsOn: ["tls-fly"] },
    { id: "verdaccio-fly", face: "platforms", connectorId: "fly", dependsOn: ["tls-fly"] },
    {
      id: "api-fly",
      face: "platforms",
      connectorId: "fly",
      dependsOn: ["gitea-fly", "verdaccio-fly"],
    },
    { id: "landing-vercel", face: "platforms", connectorId: "vercel", dependsOn: ["dns-cloudflare"] },
    { id: "pages-apps", face: "platforms", connectorId: "cloudflare", dependsOn: ["dns-cloudflare"] },
    { id: "pages-docs", face: "platforms", connectorId: "cloudflare", dependsOn: ["dns-cloudflare"] },
    { id: "pages-admin", face: "platforms", connectorId: "cloudflare", dependsOn: ["dns-cloudflare"] },
    { id: "pages-scott", face: "platforms", connectorId: "cloudflare", dependsOn: ["dns-cloudflare"] },
  ],

  gates: {
    requireApproval: ["prod"],
    budgetCeiling: { prod: 500, staging: 100, ephemeral: 20 },
    requireSmokeGreen: true,
    requireAttestation: true,
  },

  promotion: {
    chain: ["staging", "prod"],
    backupBeforeCutover: true,
  },

  rollback: {
    autoRollbackOnGateFail: true,
    keepRestorePoints: 5,
  },

  notifications: {
    route: {
      deploy: ["slack"],
      failure: ["slack", "discord"],
      ephemeral: ["discord"],
    },
  },

  ephemeral: {
    ttlMs: 72 * 60 * 60 * 1000,
    teardownOn: ["branch-webhook", "ttl"],
    costCeiling: 20,
  },
} satisfies LiquidefyTopology;
